import { createContext, type PropsWithChildren, useContext, useEffect, useState } from "react"

import { storage } from "@/common/services/localStorage.service"
import { isDevelopment } from "@/config/env"

interface DevModeContextType {
  isDevModeEnabled: boolean
  toggleDevMode: () => void
}

const DevModeContext = createContext<DevModeContextType | undefined>(undefined)

export function DevModeProvider({ children }: PropsWithChildren) {
  const [isDevModeEnabled, setIsDevModeEnabled] = useState<boolean>(() => {
    return storage.get<boolean>(storage.keys.DEV_MODE, false)
  })

  const toggleDevMode = () => {
    setIsDevModeEnabled((prev) => {
      const newValue = !prev
      storage.set(storage.keys.DEV_MODE, newValue)
      if (isDevelopment) {
        console.log(`🔧 Dev mode ${newValue ? "ENABLED" : "DISABLED"}`)
      }
      return newValue
    })
  }

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Cmd+Shift+Option+D on Mac, Ctrl+Shift+Alt+D on Windows/Linux
      const isMetaKeyPressed = navigator?.platform?.toLowerCase().includes("mac")
        ? e.metaKey
        : e.ctrlKey
      const isDKey = e.key === "d" || e.key === "D" || e.code === "KeyD"

      if (isMetaKeyPressed && e.shiftKey && e.altKey && isDKey) {
        e.preventDefault()
        e.stopPropagation()
        toggleDevMode()
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    if (isDevelopment) {
      console.log("⌨️  Dev mode shortcut active: Cmd+Shift+Option+D (or Ctrl+Shift+Alt+D)")
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  return (
    <DevModeContext.Provider value={{ isDevModeEnabled, toggleDevMode }}>
      {children}
    </DevModeContext.Provider>
  )
}

export function useDevMode() {
  const context = useContext(DevModeContext)
  if (context === undefined) {
    throw new Error("useDevMode must be used within a DevModeProvider")
  }
  return context
}
